import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
// Utilisation d'un chemin relatif pour éviter les problèmes d'alias
import { useAuthStore } from '../stores/authStore';
import notify from '../lib/notify';
import i18n from '../i18n/config';
import type { SystemErrorDetails } from '../types/notification';

/**
 * Get the API base URL with automatic HTTPS upgrade when needed
 * This ensures that if the page is loaded over HTTPS, all API calls also use HTTPS
 * to prevent mixed content security errors
 */
export const getApiBaseUrl = (): string => {
  let baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  // Fix mixed content: if the page is loaded over HTTPS, ensure API calls also use HTTPS
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseUrl.startsWith('http://')) {
    baseUrl = baseUrl.replace('http://', 'https://');
  }

  return baseUrl;
};

// Get the API base URL - used by apiClient
const API_BASE_URL = getApiBaseUrl();

// Token getter function - can be set by ApiClientProvider to get fresh tokens from Logto
type TokenGetter = () => Promise<string | null>;
let tokenGetter: TokenGetter | null = null;

/**
 * Demo mode fallback token — memory-only, never persisted to localStorage.
 * Set by DemoApp so the standard apiClient works in demo mode
 * without touching the authStore (which would persist the token).
 */
let demoTokenFallback: string | null = null;

export const setDemoTokenFallback = (token: string | null): void => {
  demoTokenFallback = token;
};

// Grace period after login - don't clear auth on 401 during this time
let loginTimestamp: number = 0;
const LOGIN_GRACE_PERIOD_MS = 3000; // 3 seconds

export const setTokenGetter = (getter: TokenGetter | null) => {
  tokenGetter = getter;
};

export const markLoginTime = () => {
  loginTimestamp = Date.now();
};

const isWithinLoginGracePeriod = (): boolean => {
  return Date.now() - loginTimestamp < LOGIN_GRACE_PERIOD_MS;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur de requête pour ajouter le token d'accès
// SYNCHRONOUS interceptor - gets token from store only
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const isFormDataRequest = typeof FormData !== 'undefined' && config.data instanceof FormData;

    // Skip auth header for public endpoints
    // Note: Use exact match or specific prefixes, NOT '/' which matches everything
    const publicEndpoints = ['/health', '/status'];
    const isPublicEndpoint = publicEndpoints.some(ep => config.url === ep || config.url?.startsWith(ep + '/'));

    if (isPublicEndpoint) {
      return config;
    }

    // Legacy login endpoint handling (for backward compatibility)
    if (config.url === '/auth/login') {
      if (config.data && typeof config.data === 'object') {
        const formData = new URLSearchParams();
        formData.append('username', config.data.email || config.data.username);
        formData.append('password', config.data.password);
        config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        config.data = formData;
      }
      return config;
    }

    // Get token from authStore (synchronous)
    const storeState = useAuthStore.getState();
    // Use authStore token first; fall back to demo token (memory-only, never persisted)
    const token = storeState.accessToken ?? demoTokenFallback;

    // Add Authorization header if we have a token
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Super-admin impersonation: send effective user id so backend treats request as that user
    const impersonatedUser = storeState.impersonatedUser;
    if (impersonatedUser?.id) {
      config.headers['X-Impersonate-User-Id'] = impersonatedUser.id;
    }

    // Set Content-Type for non-FormData requests
    if (!config.headers['Content-Type'] && !isFormDataRequest) {
      config.headers['Content-Type'] = 'application/json';
    }

    // Let browser set boundary for FormData
    if (isFormDataRequest) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse pour gérer les erreurs
// Note: With Logto, automatic token refresh is handled by the Logto SDK
// If a 401 occurs, the user should be redirected to re-authenticate via Logto
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't clear auth during login grace period - this prevents race conditions
      // where dashboard requests fire before the token is fully available
      if (isWithinLoginGracePeriod()) {
        return Promise.reject(error);
      }

      const { actions, accessToken, user } = useAuthStore.getState();

      // Demo users: attempt token refresh using the stored session token.
      // If refresh succeeds, update the in-memory fallback and retry.
      // If it fails, clear everything and redirect to the demo preparation page.
      if (user?.role === 'demo' && localStorage.getItem('demo_session_token')) {
        const sessionToken = localStorage.getItem('demo_session_token')!;
        try {
          const refreshResponse = await fetch(`${API_BASE_URL}/platform/demo/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token: sessionToken }),
          });
          if (!refreshResponse.ok) throw new Error(`Demo refresh failed: ${refreshResponse.status}`);
          const data = await refreshResponse.json() as {
            session_token: string;
            access_token: string;
            expires_in: number;
            neo4j_ready: boolean;
          };
          demoTokenFallback = data.access_token;
          actions.setTokens({ access_token: data.access_token });
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${data.access_token}`;
          return apiClient(originalRequest);
        } catch {
          localStorage.removeItem('demo_session_token');
          demoTokenFallback = null;
          window.location.href = '/demo';
          return Promise.reject(error);
        }
      }

      // One-time retry with a fresh token from Logto provider.
      // This avoids hard logout when token expiry happens mid-session.
      if (tokenGetter) {
        try {
          const freshToken = await tokenGetter();
          if (freshToken) {
            // Retry even if the value matches `accessToken` — Logto may have
            // just refreshed under the hood, or the server's clock may differ.
            // The `_retry` flag prevents an infinite loop on persistent 401.
            if (freshToken !== accessToken) {
              actions.setTokens({ access_token: freshToken });
            }
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${freshToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed after 401:', refreshError);
        }
      }

      // If refresh/retry did not succeed, consider session expired.
      if (accessToken) {
        actions.logout();
      }

      return Promise.reject(error);
    }

    // For all other errors: system-level (API/network) errors are only shown to super_admins
    const status = error.response?.status;
    const responseData = error.response?.data as { detail?: string } | string | undefined;
    const message =
      (typeof responseData === 'object' && responseData?.detail) ||
      error.message ||
      i18n.t('errors.generic');

    if (status !== 401) {
      const details: SystemErrorDetails = {
        errorType: error.name,
        stack: error.stack,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        httpStatus: status,
        endpoint: error.config?.url,
        httpMethod: error.config?.method?.toUpperCase(),
        responseSnippet:
          error.response?.data != null
            ? typeof error.response.data === 'string'
              ? error.response.data.slice(0, 500)
              : JSON.stringify(error.response.data).slice(0, 500)
            : undefined,
      };
      notify.systemError(String(message), details, i18n.t('notifications.titles.apiError'));
    }

    return Promise.reject(error);
  }
);

export default apiClient;
